// @flow
import { useCallback, useState } from 'react';

import { toPreviewDate } from '../EditMoveDateModal/correctionImpacts';
import type {
  DateFieldValue,
  PreviewStatus,
  StatusChangeHandler,
} from '../EditMoveDateModal/correctionImpacts';

type PreviewState = {
  status: PreviewStatus,
  /** The YYYY-MM-DD date the status answers. */
  date: ?string,
  /** Records the impact list shows (0 unless the preview is valid). */
  recordCount: number,
};

const IDLE_PREVIEW: PreviewState = {
  status: 'idle',
  date: null,
  recordCount: 0,
};

/**
 * The Property Move-In Date correction preview as the modal sees it (household-mi-mo-date-changes):
 * the impact list reports its status here, and Save needs a changed date that passes the form's
 * rules and whose preview loaded as valid. It stays off while the preview loads, when the server
 * rejects the date, and when the preview failed to load; the status must answer the date in the
 * field now, not one edited since.
 */
export const usePropertyMoveInCorrectionPreview = ({
  proposedDate,
  pristine,
  hasSyncErrors,
  submitting,
}: {
  proposedDate: DateFieldValue,
  pristine: boolean,
  hasSyncErrors: boolean,
  submitting: boolean,
}): {
  onPreviewStatusChange: StatusChangeHandler,
  canSave: boolean,
  hasImpactList: boolean,
} => {
  const [preview, setPreview] = useState<PreviewState>(IDLE_PREVIEW);
  const onPreviewStatusChange = useCallback(
    (status: PreviewStatus, date: ?string, recordCount: number) =>
      setPreview({ status, date, recordCount }),
    [],
  );

  return {
    onPreviewStatusChange,
    canSave:
      !submitting &&
      !pristine &&
      !hasSyncErrors &&
      preview.status === 'valid' &&
      preview.date === toPreviewDate(proposedDate),
    hasImpactList: preview.recordCount > 0,
  };
};
